/**
 * Data hooks for the Intake Gaps view + exemption affordance (Slice M /
 * ADR-0100).
 *
 *   useIntakeGaps() — GET /api/firm-success/intake-gaps. No poll: the view
 *     is a deliberate destination, not a watch surface; it refetches on
 *     focus (global default) and on exemption/document mutations via
 *     invalidation. The worker reads the firm roster with skipCache, so a
 *     fresh fetch IS fresh.
 *
 *   useFirmExemption() — POST /api/firm-success/firms/:id/exemption
 *     (mark with required reason / clear). On success:
 *       - patches the cached firm's `exemptArtifacts` from the echo and
 *         prunes the artifact from any pending `designationAlert` (the
 *         exemption review resolves the drift conflict — mirrors the
 *         server-side resolveConflict), so the open panel updates without
 *         a roster refetch;
 *       - invalidates the intake-gaps query so the chase list reflects the
 *         new posture immediately (M-06).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FirmExemptionResponseSchema,
  IntakeGapsResponseSchema,
  type ClientSuccessFirmsResponse,
  type FirmExemptionRequest,
  type FirmExemptionResponse,
  type IntakeGapsResponse,
} from "@command-center/shared";
import { apiGet, apiPost } from "./api";

export const INTAKE_GAPS_KEY = ["intake-gaps"] as const;
const FIRMS_KEY = ["firm-success", "firms"] as const;

export function useIntakeGaps() {
  return useQuery<IntakeGapsResponse, Error>({
    queryKey: INTAKE_GAPS_KEY,
    queryFn: async () => {
      const raw = await apiGet<unknown>("/api/firm-success/intake-gaps");
      const parsed = IntakeGapsResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/firm-success/intake-gaps failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

/** Patch the cached roster after an exemption echo — exempt set replaced,
 *  drift conflict for the reviewed artifact pruned. */
function applyExemption(
  prev: ClientSuccessFirmsResponse | undefined,
  patch: FirmExemptionResponse,
  reviewedArtifact: FirmExemptionRequest["artifact"],
): ClientSuccessFirmsResponse | undefined {
  if (!prev) return prev;
  let touched = false;
  const next = prev.firms.map((f) => {
    if (f.id !== patch.id) return f;
    touched = true;
    const remainingConflicts =
      f.designationAlert?.conflictingExemptions.filter(
        (a) => a !== reviewedArtifact,
      ) ?? [];
    return {
      ...f,
      exemptArtifacts: patch.exemptArtifacts,
      designationAlert:
        f.designationAlert === null || remainingConflicts.length === 0
          ? null
          : { ...f.designationAlert, conflictingExemptions: remainingConflicts },
    };
  });
  return touched ? { firms: next } : prev;
}

export function useFirmExemption() {
  const queryClient = useQueryClient();
  return useMutation<
    FirmExemptionResponse,
    Error,
    { firmId: string } & FirmExemptionRequest
  >({
    mutationFn: async ({ firmId, ...body }) => {
      const raw = await apiPost<unknown>(
        `/api/firm-success/firms/${encodeURIComponent(firmId)}/exemption`,
        body,
      );
      const parsed = FirmExemptionResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/firm-success/firms/:id/exemption failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result, vars) => {
      queryClient.setQueryData<ClientSuccessFirmsResponse | undefined>(
        FIRMS_KEY,
        (prev) => applyExemption(prev, result, vars.artifact),
      );
      void queryClient.invalidateQueries({ queryKey: INTAKE_GAPS_KEY });
    },
  });
}
